import primaryComponents from "../../primaryComponents";
import { useTable } from "react-table";
import { useMemo, useContext } from "react";
import { ToastContext } from "../../../hooks/context";
import secondaryComponents from "..";
import {
  cleanObject,
  formatNumberWithCommas,
  truncateString,
} from "../../../helpers";
import { bulkTransferstockDelete } from "../../../services/inventory";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import styles from "./BulkTransferTable.module.scss";
import { DeleteIcon } from "../../../assets/svgIcons";

export const useBulkTransferTable = ({
  transferData,
  setTransferData,
  validation,
  isReviewMode,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = validation;
  const params = useParams();
  const triggerToast = useContext(ToastContext);

  const isObjectEmpty = (obj) => {
    return Object.keys(obj).length === 0;
  };

  const deleteRow = (index) => {
    const newData = [...instance?.data];
    const deleteStockTransferItem = cleanObject({
      stock_transfer_item_id: newData[index]?.stock_transfer_item_id,
    });

    if (!isObjectEmpty(deleteStockTransferItem)) {
      bulkTransferstockDelete({
        data: deleteStockTransferItem,
        id: params?.id,
      })
        .then(() => {
          newData.splice(index, 1);
          setTransferData(newData);
          triggerToast("Stock Transfer Removed", "success");
        })
        .catch((error) => {
          console.error("Error deleting stock transfer items:", error);
        });
    } else {
      triggerToast("Stock Transfer Removed", "success");
      newData.splice(index, 1);
      setTransferData(newData);
    }
  };

  const columns = useMemo(() => {
    const headers = [
      {
        Header: "Product Name",
        accessor: "name",
        Cell: ({ cell: { value }, row }) => (
          <span className="d-flex align-items-center ml-3">
            <secondaryComponents.ConcatenateWithTooltip
              value={value}
              noOfCharacters={200}
            />
          </span>
        ),
      },
      {
        Header: "Variant",
        accessor: (row) => row.variant || row.variant_name || "N/A",
        Cell: ({ cell: { row } }) =>
          truncateString(
            row?.original?.variant || row?.original?.variant_name || "N/A",
            200,
          ),
      },
      {
        Header: "Quantity",
        accessor: "stock_quantity",
        Cell: ({ cell: { value } }) => (
          <span
            style={{
              color: "#19AE57",
              backgroundColor: "#E6FFF0",
            }}
          >
            {formatNumberWithCommas(value)}
          </span>
        ),
      },
      {
        Header: "Quantity to Transfer",
        accessor: "quantity",
        Cell: ({ cell: { value }, row }) => (
          <span className="d-flex align-items-center">
            <primaryComponents.InputFieldV2
              name={`transferData.${row?.index}.quantity`}
              control={control}
              classNames={`purple--border white ${styles.inputField}`}
              bodyStyle={{ width: "100px" }}
              isRequired
              type="number"
              step={0.01}
              min={0.01}
              placeholderContent="Enter value"
              value={value}
              errorMessage={errors?.transferData?.[row?.index]?.quantity}
              onKeyUp={() =>
                errors?.transferData?.[row?.index]?.quantity !== undefined &&
                trigger(`transferData.${row?.index}.quantity`)
              }
              onChange={({ target: { value } }) => {
                const newData = [...instance?.data];
                newData[row?.index].quantity = value;
                setTransferData(newData);
              }}
              isDisabled={isReviewMode}
            />
            <span className="ml-1">{row?.original?.unit}</span>
          </span>
        ),
      },
      {
        Header: "Cost Price / Unit",
        accessor: "cost_price",
        Cell: ({ cell: { value } }) => (
          <span>{value != null ? formatNumberWithCommas(value) : "—"}</span>
        ),
      },
      {
        Header: "Total Transfer Value",
        accessor: (row) => {
          const qty = parseFloat(row.quantity) || 0;
          const cost = parseFloat(row.cost_price) || 0;
          return qty * cost;
        },
        id: "total_transfer_value",
        Cell: ({ cell: { value }, row }) => {
          const qty = parseFloat(row?.original?.quantity) || 0;
          const cost = parseFloat(row?.original?.cost_price) || 0;
          const total = qty * cost;
          return (
            <span style={{ fontWeight: 600 }}>
              {formatNumberWithCommas(total.toFixed(2))}
            </span>
          );
        },
      },

      {
        Header: "Use prices across other stores?",
        accessor: "update_destination_pricing",
        Cell: ({ row }) => {
          const uuid = uuidv4();

          const handleRadioChange = (value) => {
            const newData = [...instance?.data];
            newData[row.index].update_destination_pricing = value;
            setTransferData(newData);
          };

          return (
            <div
              className="d-flex mb-1 justify-content-center align-items-center py-1"
              style={{
                gap: "4px",
                background: "#fff",
                borderRadius: "16px",
                minHeight: "15px",
                maxWidth: "140px",
              }}
            >
              <div
                className="d-flex align-items-center"
                style={{ gap: "12px" }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ gap: "4px" }}
                >
                  <primaryComponents.RadioButton
                    name={`yesNo-transferData${uuid}`}
                    id={`yes-${uuid}`}
                    value={true}
                    checked={row?.original?.update_destination_pricing}
                    isDisabled={isReviewMode}
                    setRadioValue={handleRadioChange}
                  />
                  <span style={{ fontSize: "13px" }}>Yes</span>
                </div>

                <div
                  style={{
                    width: "2px",
                    height: "20px",
                    backgroundColor: "#ccc",
                  }}
                ></div>

                <div
                  className="d-flex align-items-center"
                  style={{ gap: "4px" }}
                >
                  <primaryComponents.RadioButton
                    name={`yesNo-transferData${uuid}`}
                    id={`no-${uuid}`}
                    value={false}
                    isDisabled={isReviewMode}
                    checked={!row?.original?.update_destination_pricing}
                    setRadioValue={handleRadioChange}
                  />
                  <span style={{ fontSize: "13px" }}>No</span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Action",
        accessor: "",
        Cell: ({ row }) => (
          <>
            {!isReviewMode ? (
              <span onClick={() => deleteRow(row?.index)}>
                <DeleteIcon />
              </span>
            ) : null}
          </>
        ),
      },
    ];

    return headers;
  }, [errors]);

  const instance = useTable({
    columns,
    data: transferData,
    autoResetPage: false,
  });
  return { instance };
};
